
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AnimatedPage from "@/components/AnimatedPage";
import NavHeader from "@/components/NavHeader";
import { getChitPools, getLoans } from "@/lib/api";
import { ChitPool, Loan, Member } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface Customer {
  id: string;
  name: string;
  whatsappNumber: string;
  chitPools: ChitPool[];
  loans: Loan[];
}

const Customers = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const chitPools = await getChitPools();
        const loans = await getLoans();
        
        // Extract all members from chit pools
        const allMembers: Member[] = [];
        chitPools.forEach((pool) => {
          allMembers.push(...pool.members);
        });
        
        // Create a map to deduplicate customers by whatsapp number
        const customerMap = new Map<string, Customer>();
        
        // Add chit pool members
        allMembers.forEach((member) => {
          if (!member.whatsappNumber) return;
          
          if (!customerMap.has(member.whatsappNumber)) {
            customerMap.set(member.whatsappNumber, {
              id: member.id,
              name: member.name,
              whatsappNumber: member.whatsappNumber,
              chitPools: [],
              loans: []
            });
          }
          
          const customer = customerMap.get(member.whatsappNumber);
          if (customer) {
            const associatedPool = chitPools.find(pool => pool.id === member.chitPoolId);
            if (associatedPool && !customer.chitPools.some(p => p.id === associatedPool.id)) {
              customer.chitPools.push(associatedPool);
            }
          }
        });
        
        // Add loan customers
        loans.forEach((loan) => {
          if (!loan.whatsappNumber) return;
          
          if (!customerMap.has(loan.whatsappNumber)) {
            customerMap.set(loan.whatsappNumber, {
              id: loan.id,
              name: loan.customer_name,
              whatsappNumber: loan.whatsappNumber,
              chitPools: [],
              loans: []
            });
          }
          
          const customer = customerMap.get(loan.whatsappNumber);
          if (customer && !customer.loans.some(l => l.id === loan.id)) {
            customer.loans.push(loan);
          }
        });
        
        const customerList = Array.from(customerMap.values());
        setCustomers(customerList);
        setFilteredCustomers(customerList);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredCustomers(customers);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = customers.filter(customer => 
        customer.name.toLowerCase().includes(query) || 
        customer.whatsappNumber.includes(query)
      );
      setFilteredCustomers(filtered);
    }
  }, [searchQuery, customers]);

  const handleCustomerClick = (customerId: string) => {
    setSelectedCustomer(selectedCustomer === customerId ? null : customerId);
  };

  if (loading) {
    return (
      <AnimatedPage>
        <div className="container max-w-4xl py-8">
          <NavHeader title="My Customers" backTo="/home" />
          <div className="flex justify-center items-center mt-12">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
            <span className="ml-3">Loading customers...</span>
          </div>
        </div>
      </AnimatedPage>
    );
  }

  return (
    <AnimatedPage>
      <div className="container max-w-4xl py-8">
        <NavHeader title="My Customers" backTo="/home" />
        
        <div className="mt-6 mb-6 relative">
          <Input
            type="text"
            placeholder="Search by name or phone number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
        </div>
        
        {filteredCustomers.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No customers found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredCustomers.map((customer) => (
              <Collapsible 
                key={customer.id}
                open={selectedCustomer === customer.id}
                onOpenChange={() => {}}
              >
                <Card className="p-4">
                  <CollapsibleTrigger asChild className="w-full">
                    <Button 
                      variant="ghost" 
                      className="w-full flex justify-between items-center p-2 hover:bg-transparent"
                      onClick={() => handleCustomerClick(customer.id)}
                    >
                      <div className="flex flex-col items-start">
                        <h3 className="font-medium text-lg">{customer.name}</h3>
                        <p className="text-sm text-muted-foreground">{customer.whatsappNumber}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {customer.chitPools.length > 0 && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                            {customer.chitPools.length} Chit{customer.chitPools.length > 1 ? 's' : ''}
                          </span>
                        )}
                        {customer.loans.length > 0 && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                            {customer.loans.length} Loan{customer.loans.length > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </Button>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent className="mt-4 space-y-4">
                    {customer.chitPools.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2">Chit Pools</h4>
                        <div className="space-y-2">
                          {customer.chitPools.map((pool) => (
                            <div 
                              key={pool.id} 
                              className="p-3 bg-blue-50 rounded-md cursor-pointer hover:bg-blue-100"
                              onClick={() => navigate(`/chit-pools/${pool.id}`)}
                            >
                              <div className="flex justify-between">
                                <span className="font-medium">{pool.name}</span>
                                <span>₹{pool.amount.toLocaleString()}</span>
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {pool.months} months
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {customer.loans.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2">Loans</h4>
                        <div className="space-y-2">
                          {customer.loans.map((loan) => (
                            <div 
                              key={loan.id} 
                              className="p-3 bg-green-50 rounded-md cursor-pointer hover:bg-green-100"
                              onClick={() => navigate(`/loans/${loan.id}`)}
                            >
                              <div className="flex justify-between">
                                <span className="font-medium">₹{loan.amount.toLocaleString()}</span>
                                <span>{loan.interest_rate}% interest</span>
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {loan.tenure} months tenure
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            ))}
          </div>
        )}
      </div>
    </AnimatedPage>
  );
};

export default Customers;
